import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";

import lessonServices from "../../../services/lessonServices";
import LoadingSpinner from "../../LoadingSpinner";
import Modal from "../../Modal";

const WatchVideoModal = forwardRef(({}, ref) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [video, setVideo] = useState(null);
  const [thumbnail, setThumbnail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal(selectedLessonID, videoType) {
      setIsOpen(true);
      fetchVideo(selectedLessonID, videoType);
    },
    closeModal() {
      setIsOpen(false);
      setVideo(null); // Reset video when closing
      setIsPlaying(false);
    },
  }));

  const handleCloseModal = () => {
    setVideo(null); // Reset video when closing
    setIsPlaying(false);
    setIsOpen(false);
  };

  const fetchVideo = async (lessonID, videoType) => {
    setIsLoading(true);
    try {
      const response = await lessonServices.getVideo(lessonID, videoType);
      if (response?.video) {
        setVideo(response);
        const thumbnailDataUrl = await getThumbnail(response.video, 1);
        setThumbnail(thumbnailDataUrl);
      } else {
        setVideo(null); // Handle case where video URL is missing
      }
    } catch (error) {
      console.error("Error fetching video:", error);
    } finally {
      setIsLoading(false);
    }
  };
  function getThumbnail(videoUrl, timeInSeconds = 1) {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.crossOrigin = "anonymous"; // Allow cross-origin if needed
      video.src = videoUrl;
      video.preload = "metadata";

      video.addEventListener("loadeddata", () => {
        video.currentTime = timeInSeconds;
      });

      video.addEventListener("seeked", () => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg")); // or 'image/png'
      });

      video.onerror = () => reject("Failed to load video");
    });
  }

  return (
    <Modal isOpen={isOpen} onClose={handleCloseModal}>
      {isLoading ? (
        <div className="flex items-center justify-center py-5">
          <LoadingSpinner color="green" />
        </div>
      ) : video ? (
        <div className="relative aspect-video w-full">
          {!isPlaying ? (
            <div className="relative h-full w-full cursor-pointer" onClick={() => setIsPlaying(true)}>
              <img
                src={thumbnail.thumbnail || thumbnail} // Default thumbnail fallback
                alt="Video Thumbnail"
                className="h-full w-full rounded-lg object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black bg-opacity-30">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="white"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="white"
                  className="h-16 w-16 transform transition hover:scale-110"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v18l15-9L5 3z" />
                </svg>
              </div>
            </div>
          ) : (
            <video
              src={video.video}
              controls
              autoPlay
              controlsList="nodownload"
              className="h-full w-full rounded-lg"
            >
              Your browser does not support the video tag.
            </video>
          )}
        </div>
      ) : (
        <p className="flex min-h-[45vh] items-center justify-center text-center text-gray-500">
          {t("no_video_available")}
        </p>
      )}
    </Modal>
  );
});

export default WatchVideoModal;
